import { AsyncPipe, DecimalPipe, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { PageToolbarComponent, NotificationEmptyComponent } from '@next/core-lib/components';
import { TranslatePipe } from '@next/core-lib/i18n';
import { SiteActions, SiteSelectors, SiteState } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { MonthPickerComponent } from '../../../shared/components/month-picker/month-picker.component';
import * as TechnicalResultActions from '../../store/actions/technical-result.actions';
import { getTechnicalResultStateByDate } from '../../store/selectors/technical-result.selectors';

@Component({
  selector: 'app-technical-result',
  templateUrl: './technical-result.component.html',
  styleUrls: ['./technical-result.component.scss'],
  imports: [
    PageToolbarComponent,
    MonthPickerComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NotificationEmptyComponent,
    AsyncPipe,
    DecimalPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class TechnicalResultComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);

  public pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));

  public dataSource = new MatTableDataSource<TechnicalResultTableRow>();
  public validDateFrom: Date;
  public validDateTo = new Date();
  public columns: Column[] = [
    {
      id: 'key',
      label: '',
      type: 'string',
    },
    {
      id: 'monthValue',
      label: 'technical-result.month',
      type: 'number',
    },
    {
      id: 'quarterValue',
      label: 'technical-result.quarter',
      type: 'number',
    },
    {
      id: 'halfYearValue',
      label: 'technical-result.half-year',
      type: 'number',
    },
    {
      id: 'yearValue',
      label: 'technical-result.year',
      type: 'number',
    },
    {
      id: 'lastYearValue',
      label: 'technical-result.last-year',
      type: 'number',
    },
  ];
  private readonly destroy$ = new Subject<void>();
  private getTechnicalResultStateByDate$ = this.store.select(getTechnicalResultStateByDate);

  ngOnInit(): void {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: 'technical-result.title' }));

    const month = new Date().getMonth();
    if (month < 3) {
      this.validDateFrom = new Date(new Date().getFullYear() - 1, month + 9, 1);
    } else {
      this.validDateFrom = new Date(new Date().getFullYear(), month - 3, 1);
    }

    const formatWholeNumber = '0.0-0';
    const formatNumber = '0.2-2';

    this.getTechnicalResultStateByDate$
      .pipe(
        filter((values) => !!values),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        const data: TechnicalResultTableRow[] = [
          this.getTableSubheader('herd'),
          this.getTableRow(result.herd, 'milkCows', formatWholeNumber),
          this.getTableRow(result.herd, 'dryCows', formatWholeNumber),
          this.getTableRow(result.herd, 'youngStockGt1', formatWholeNumber),
          this.getTableRow(result.herd, 'youngStockLt1', formatWholeNumber),
          this.getTableSubheader('feedConsumption'),
          this.getTableRow(result.feedConsumption, 'totalPerCowPerDay', formatNumber),
          this.getTableRow(result.feedConsumption, 'totalRoughagePerCowPerDay', formatNumber),
          this.getTableRow(result.feedConsumption, 'totalConcentratePerCowPerDay', formatNumber),
          this.getTableRow(result.feedConsumption, 'totalMineralPerCowPerDay', formatNumber),
          this.getTableSubheader('feedCosts'),
          this.getTableRow(result.feedCost, 'totalPerCowPerDay', formatNumber),
          this.getTableRow(result.feedCost, 'totalRoughagePerCowPerDay', formatNumber),
          this.getTableRow(result.feedCost, 'totalConcentratePerCowPerDay', formatNumber),
          this.getTableRow(result.feedCost, 'totalMineralPerCowPerDay', formatNumber),
          this.getTableSubheader('milkYield'),
          this.getTableRow(result.milkYield, 'kgMilkPerCowPerDay', formatNumber),
          this.getTableRow(result.milkYield, 'kgFPCMPerCowPerDay', formatNumber),
          this.getTableRow(result.milkYield, 'milkYieldPerKg', formatNumber),
          this.getTableSubheader('incomeOverFeed'),
          this.getTableRow(result.incomeOverFeed, 'feedBalance', formatNumber),
          this.getTableRow(result.incomeOverFeed, 'feedBalanceExclRoughage', formatNumber),
        ];

        this.dataSource.data = data;
        this.setSubLabelValues(result.herd, 'monthValue', PeriodKeys.month);
        this.setSubLabelValues(result.herd, 'quarterValue', PeriodKeys.quarter);
        this.setSubLabelValues(result.herd, 'halfYearValue', PeriodKeys.halfYear);
        this.setSubLabelValues(result.herd, 'yearValue', PeriodKeys.year);
        this.setSubLabelValues(result.herd, 'lastYearValue', PeriodKeys.lastYear);
      });
    this.onDateChanged(
      new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0)).toISOString(),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  public columnIds = (): string[] => this.columns.map((item) => item.id);
  public hasData = (): boolean => this.dataSource?.data?.length > 0;
  public allignRight = (type: string): boolean => type === 'number';
  public onDateChanged(date: string): void {
    this.store.dispatch(
      TechnicalResultActions.loadTechnicalResultRequest({
        startDate: date,
        endDate: date,
      }),
    );
  }
  public onCheckValidity(date: Date): boolean {
    return !(date > new Date());
  }

  private getTableRow(
    items: ReportRowItem[],
    key: string,
    format: string,
  ): TechnicalResultTableRow {
    return {
      isHeader: false,
      key,
      format,
      monthValue: this.getPeriod(items, PeriodKeys.month)[key] as number,
      quarterValue: this.getPeriod(items, PeriodKeys.quarter)[key] as number,
      halfYearValue: this.getPeriod(items, PeriodKeys.halfYear)[key] as number,
      yearValue: this.getPeriod(items, PeriodKeys.year)[key] as number,
      lastYearValue: this.getPeriod(items, PeriodKeys.lastYear)[key] as number,
    };
  }

  private getTableSubheader(key: string): TechnicalResultTableRow {
    return {
      isHeader: true,
      key,
      format: null,
      monthValue: 0,
      quarterValue: 0,
      halfYearValue: 0,
      yearValue: 0,
      lastYearValue: 0,
    };
  }

  private getPeriod = (items: ReportRowItem[], period: PeriodKeys): ReportRowItem =>
    items.find((item) => item.header === period);

  private getColumn = (id: string): Column => this.columns.find((item) => item.id === id);

  private setSubLabelValues(data: ReportRowItem[], columnId: string, periodName: PeriodKeys): void {
    const period = this.getPeriod(data, periodName);
    const column = this.getColumn(columnId);
    column.dateFrom = period.dateFrom;
    const to = new Date(period.dateTo);
    to.setMonth(to.getMonth() - 1);
    column.dateTo = to.toISOString();
  }
}

enum PeriodKeys {
  'month' = 'month',
  'quarter' = 'quarter',
  'halfYear' = 'half-year',
  'year' = 'year',
  'lastYear' = 'last-year',
}

interface TechnicalResultTableRow {
  isHeader: boolean;
  key: string;
  format: string;
  monthValue: number;
  quarterValue: number;
  halfYearValue: number;
  yearValue: number;
  lastYearValue: number;
}

interface Column {
  id: string;
  label: string;
  dateFrom?: string;
  dateTo?: string;
  type: 'string' | 'number';
}

interface ReportRowItem {
  [key: string]: string | number;
  header: string;
  dateFrom: string;
  dateTo: string;
}
